/**
 * Shared Zustand store for Hearings — now wired to the real API.
 */
import { create } from 'zustand';
import { toast } from 'sonner';
import * as hearingsApi from './hearingsApi';
import { getApiErrorMessage } from './api';

export type HearingOutcome = 'won' | 'lost' | 'adjourned' | 'settled' | 'directions' | 'part_heard' | 'dismissed' | 'withdrawn' | 'reserved' | '';

export interface Hearing {
  id: string;
  matterId: string;
  matterName: string;
  matterNumber: string;
  date: string;
  time: string;
  durationLabel: string;
  durationMinutes?: number;
  type: string;
  status:
    | 'scheduled'
    | 'confirmed'
    | 'in_progress'
    | 'tentative'
    | 'awaiting_appearance'
    | 'awaiting_compliance'
    | 'adjourned'
    | 'order_reserved'
    | 'judgment_pending'
    | 'completed'
    | 'vacated'
    | 'no_show'
    | 'disposed';
  court: string;
  courtId?: string | null;
  courtroom: string;
  judge: string;
  judgeId?: string | null;
  leadBarrister: string;
  leadBarristerUserId: string | null;
  juniorBarrister: string;
  juniorBarristerUserId: string | null;
  isVirtual: boolean;
  videoLink: string;
  notes: string;
  outcome: string;
  outcomeType?: string;
  judgmentReserved?: boolean;
  costsOrdered?: boolean;
  judicialRemarks?: string;
  orders: string;
  counselNotes: string;
  nextHearingDate: string;
  createdAt: number;
  costsPosition?: string;
  deadlines?: string;
  advocateNotes?: string;
  preparationChecklist?: Record<string, boolean>;
  updatedAt?: number;
}

interface HearingsState {
  hearings: Hearing[];
  loading: boolean;
  error: string | null;
  fetchHearings: () => Promise<void>;
  /** Put a hearing fetched directly by id into the store (insert or replace). */
  upsertHearing: (hearing: Hearing) => void;
  addHearing: (input: {
    matterId: string;
    date: string;
    time: string;
    durationLabel: string;
    type: string;
    court: string;
    courtroom: string;
    judge: string;
    courtId?: string;
    leadBarrister: string;
    leadBarristerUserId?: string | null;
    juniorBarrister: string;
    juniorBarristerUserId?: string | null;
    isVirtual: boolean;
    videoLink: string;
    notes: string;
  }) => Promise<Hearing | null>;
  /**
   * Optimistic, then saved. Resolves true once the server has accepted the change, false if it
   * was refused (already rolled back and the user told why) — callers confirm success only on true.
   */
  updateHearing: (id: string, patch: Partial<Hearing>) => Promise<boolean>;
  deleteHearing: (id: string) => void;
  getHearingsForMatter: (matterId: string) => Hearing[];
}

export const useHearingsStore = create<HearingsState>()((set, get) => ({
  hearings: [],
  loading: false,
  error: null,

  fetchHearings: async () => {
    set({ loading: true, error: null });
    try {
      const hearings = await hearingsApi.fetchHearings();
      set({ hearings, loading: false });
    } catch (err) {
      console.error('Failed to fetch hearings:', err);
      set({ loading: false, error: 'Failed to load hearings' });
    }
  },

  upsertHearing: (hearing) => {
    set((s) => ({
      hearings: s.hearings.some((h) => h.id === hearing.id)
        ? s.hearings.map((h) => (h.id === hearing.id ? hearing : h))
        : [...s.hearings, hearing],
    }));
  },

  addHearing: async (input) => {
    const hearing: Hearing = {
      id: `temp-${Date.now()}`,
      matterId: input.matterId,
      matterName: '',
      matterNumber: '',
      date: input.date,
      time: input.time,
      durationLabel: input.durationLabel,
      type: input.type,
      status: 'scheduled',
      court: input.court,
      courtroom: input.courtroom,
      judge: input.judge,
      leadBarrister: input.leadBarrister,
      leadBarristerUserId: input.leadBarristerUserId ?? null,
      juniorBarrister: input.juniorBarrister,
      juniorBarristerUserId: input.juniorBarristerUserId ?? null,
      isVirtual: input.isVirtual,
      videoLink: input.videoLink,
      notes: input.notes,
      outcome: '',
      orders: '',
      counselNotes: '',
      nextHearingDate: '',
      createdAt: Date.now(),
    };
    set((s) => ({ hearings: [hearing, ...s.hearings] }));

    // Parse duration label to minutes
    let durationMinutes = 60;
    const durMatch = input.durationLabel.match(/(\d+)/);
    if (durMatch) {
      const num = parseInt(durMatch[1] ?? '60', 10);
      if (input.durationLabel.includes('day')) durationMinutes = num * 480;
      else if (input.durationLabel.includes('hour')) durationMinutes = num * 60;
      else durationMinutes = num;
    }

    // Map type label back to API enum
    const typeMap: Record<string, string> = {
      'First listing': 'first_listing', 'CMC': 'case_management', 'PTR': 'pre_trial_review',
      'Application': 'interlocutory', 'Trial': 'trial', 'Sentencing': 'sentencing',
      'Appeal': 'appeal', 'Mention': 'mention', 'Bail': 'bail', 'Costs': 'costs',
      'Mediation': 'mediation', 'Hearing': 'other', 'PTPH': 'ptph',
    };

    try {
      const real = await hearingsApi.createHearing({
        caseId: input.matterId,
        hearingDate: `${input.date}T${input.time}:00Z`,
        durationMinutes,
        hearingType: typeMap[input.type] ?? 'other',
        courtId: input.courtId || undefined,
        courtroom: input.courtroom || undefined,
        notes: input.notes || undefined,
        isVirtual: input.isVirtual,
        videoLink: input.videoLink || undefined,
      });
      set((s) => ({ hearings: s.hearings.map((h) => h.id === hearing.id ? real : h) }));
      return real;
    } catch (err) {
      console.error('Failed to create hearing:', err);
      set((s) => ({ hearings: s.hearings.filter((h) => h.id !== hearing.id) }));
      toast.error(getApiErrorMessage(err, 'The hearing could not be booked'));
      return null;
    }
  },

  updateHearing: async (id, patch) => {
    // Snapshot before mutation for reliable local rollback
    const previous = get().hearings.find((h) => h.id === id);

    set((s) => ({
      hearings: s.hearings.map((h) => h.id === id ? { ...h, ...patch, updatedAt: Date.now() } : h),
    }));

    const apiPatch: Record<string, unknown> = {};
    const current = get().hearings.find((h) => h.id === id);

    // status
    if (patch.status !== undefined) {
      const statusMap: Record<string, string> = {
        scheduled: 'scheduled', confirmed: 'confirmed', completed: 'completed',
        adjourned: 'adjourned', vacated: 'vacated',
      };
      apiPatch.status = statusMap[patch.status] ?? patch.status;
    }

    // hearingDate: combine date + time (use current values as fallback)
    if (patch.date !== undefined || patch.time !== undefined) {
      const d = patch.date ?? current?.date;
      const t = patch.time ?? current?.time ?? '10:00';
      if (d) apiPatch.hearingDate = `${d}T${t}:00Z`;
    }

    // durationMinutes
    if (patch.durationMinutes !== undefined) apiPatch.durationMinutes = patch.durationMinutes;

    // hearingType: reverse map from display label → API enum
    if (patch.type !== undefined) {
      const typeRevMap: Record<string, string> = {
        'Trial': 'trial', 'CMC': 'case_management', 'PTR': 'pre_trial_review',
        'PTPH': 'ptph', 'Application': 'interlocutory', 'Hearing': 'other',
        'Other': 'other', 'First listing': 'first_listing', 'Sentencing': 'sentencing',
        'Appeal': 'appeal', 'Mention': 'mention', 'Bail': 'bail', 'Costs': 'costs',
        'Mediation': 'mediation',
      };
      apiPatch.hearingType = typeRevMap[patch.type] ?? 'other';
    }

    // court
    if (patch.courtId !== undefined) apiPatch.courtId = patch.courtId || null;
    if (patch.courtroom !== undefined) apiPatch.courtroom = patch.courtroom;

    // judge
    if (patch.judgeId !== undefined) apiPatch.judgeId = patch.judgeId || null;

    // virtual hearing
    if (patch.isVirtual !== undefined) apiPatch.isVirtual = patch.isVirtual;
    if (patch.videoLink !== undefined) apiPatch.videoLink = patch.videoLink;

    // text fields
    if (patch.outcome !== undefined) apiPatch.outcome = patch.outcome;
    if (patch.notes !== undefined) apiPatch.notes = patch.notes;
    if (patch.nextHearingDate !== undefined) apiPatch.nextHearingDate = patch.nextHearingDate;

    // Post-hearing court record fields
    if (patch.orders !== undefined) apiPatch.courtOrders = patch.orders;
    if (patch.counselNotes !== undefined) apiPatch.counselNotes = patch.counselNotes;
    if (patch.costsPosition !== undefined) apiPatch.costsPosition = patch.costsPosition;
    if (patch.deadlines !== undefined) apiPatch.courtDeadlines = patch.deadlines;

    // New structured outcome fields
    if (patch.outcomeType !== undefined) apiPatch.outcomeType = patch.outcomeType;
    if (patch.judgmentReserved !== undefined) apiPatch.judgmentReserved = patch.judgmentReserved;
    if (patch.costsOrdered !== undefined) apiPatch.costsOrdered = patch.costsOrdered;
    if (patch.judicialRemarks !== undefined) apiPatch.judicialRemarks = patch.judicialRemarks;
    if ((patch as any).courtOrders !== undefined) apiPatch.courtOrders = (patch as any).courtOrders;

    if (Object.keys(apiPatch).length === 0) return true;
    try {
      await hearingsApi.updateHearing(id, apiPatch);
      return true;
    } catch (err) {
      console.error('Failed to update hearing:', err);
      // Local rollback first — no network dependency
      if (previous) {
        set((s) => ({ hearings: s.hearings.map((h) => h.id === id ? previous : h) }));
      }
      toast.error(getApiErrorMessage(err, 'Failed to save hearing update — please try again'));
      // Best-effort server re-sync
      get().fetchHearings();
      return false;
    }
  },

  deleteHearing: (id) => {
    const removed = get().hearings.find((h) => h.id === id);
    set((s) => ({ hearings: s.hearings.filter((h) => h.id !== id) }));

    hearingsApi.deleteHearing(id).catch((err) => {
      console.error('Failed to delete hearing:', err);
      if (removed) set((s) => ({ hearings: [removed, ...s.hearings] }));
    });
  },

  getHearingsForMatter: (matterId) =>
    get().hearings.filter((h) => h.matterId === matterId).sort((a, b) => b.createdAt - a.createdAt),
}));
